import { useRef, useState } from "react";
import { AxiosInstance } from "axios";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Column, Spacer } from "../../../components/Layout/Layout";
import {
  IAllIntegrationTypes,
  IIntegrationType,
  IOrgIntegration,
} from "../../../core/api/organizations/types";
import {
  createIntegration,
  updateIntegration,
} from "../../../core/api/organizations/organizations";
import { notify } from "../../../ui-lib/components/Alerts/Toast";
import { notifyApiErrors } from "../../../core/helpers/helpers";
import { IntegrationSettings } from "./IntegrationSetting/IntegrationSettings";
import { CustomSettings } from "./CustomSettings/CustomSettings";
import useTranslations from "../../../core/i18n/useTranslations";
import Dropdown from "../../../ui-lib/components/Dropdown/Dropdown";
import TextInput from "../../../ui-lib/components/Inputs/TextInput";
import PrimeModal from "../../../ui-lib/components/PrimeModal/PrimeModal";
import Divider from "../../../ui-lib/components/Divider/Divider";
import CollapsibleBlock, {
  CollapsibleBlockActions,
} from "../../../ui-lib/components/CollapsibleBlock/CollapsibleBlock";
import useUser from "../../../core/user/useUser";

export const AddIntegartionsModal = ({
  open,
  orgId,
  integrationData,
  types,
  isNew,
  close,
  getOrganizationIntegrations,
}: {
  open: boolean;
  orgId: string;
  integrationData?: IOrgIntegration;
  types?: IAllIntegrationTypes;
  isNew?: boolean;
  close: () => void;
  getOrganizationIntegrations: (
    orgId: string,
    authenticatedRequest: AxiosInstance
  ) => Promise<void>;
}) => {
  const t = useTranslations();
  const { authenticatedRequest, config } = useUser();
  const customSettingRef = useRef<CollapsibleBlockActions>(null);
  const integrationSettings = useRef<CollapsibleBlockActions>(null);

  const integrationWritePermission = config?.show.includes(
    "PlanningEngineIntegrationWrite"
  );

  const methods = useForm<IOrgIntegration>({
    defaultValues: integrationData,
  });

  const {
    formState: { errors, isDirty },
    control,
    handleSubmit,
    getValues,
    reset,
    trigger,
  } = methods;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<
    IIntegrationType | undefined
  >(types?.[integrationData?.integrationType as keyof IAllIntegrationTypes]);

  const onClose = () => {
    close();
    reset();
  };

  const onSubmit = async (data: IOrgIntegration) => {
    setIsLoading(true);

    try {
      if (!isNew && integrationData) {
        await updateIntegration(
          orgId,
          integrationData.integrationId,
          authenticatedRequest,
          data
        );
        onClose();
      } else {
        await createIntegration(orgId, authenticatedRequest, data);
        onClose();
      }
      notify({
        message: isNew
          ? t("Organizations:instance_created_successfully")
          : t("Organizations:instance_edited_successfully"),
      });
      await getOrganizationIntegrations(orgId, authenticatedRequest);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PrimeModal
      withHeader
      withFooter
      loading={isLoading}
      fixedWidth="848px"
      isOpen={open}
      onClose={() => {
        reset();
        onClose();
      }}
      header={
        isNew
          ? t("Organizations:add_instance")
          : t("Organizations:edit_instance")
      }
      submitBtn={{
        text: t("Common:save_changes"),
        onClick: () => {
          trigger().then((valid) => {
            if (!valid) {
              if (errors.expectedUserSettings?.length) {
                customSettingRef?.current?.open();
              }
              if (errors.integrationSettings?.length) {
                integrationSettings?.current?.open();
              }
            }
          });
          handleSubmit(onSubmit)();
        },
        disabled: !isDirty,
        hidden: !integrationWritePermission,
      }}
      cancelBtn={{
        text: t("Common:cancel"),
        onClick: () => {
          reset();
          onClose();
        },
      }}
    >
      <p>{t("Organizations:new_integration_description")}</p>
      <Spacer size={16} />
      <FormProvider {...methods}>
        <Controller
          name="integrationType"
          control={control}
          render={({ field }) => {
            const integrationTypes = types
              ? Object.keys(types).map((type) => ({
                  id: type,
                  name: type,
                  isSelected: type === field.value,
                }))
              : [];
            return (
              <Dropdown
                items={integrationTypes}
                selectedItem={integrationTypes.find((item) => item.isSelected)}
                onSelectItem={(val) => {
                  const { id } = val;
                  field.onChange(id);
                  setSelectedType(types?.[id]);
                }}
                title={t("Common:integration")}
                disabled={!!integrationData?.integrationType || !types}
              />
            );
          }}
        />

        <Spacer size={16} />
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <TextInput
              required
              disabled={!isNew}
              inputRef={field.ref}
              label={t("Organizations:instance_name")}
              placeholder={t("Organizations:instance_name_placeholder")}
              value={field.value}
              onChange={field.onChange}
              validationError={errors.name && t("Errors:input_field_required")}
            />
          )}
        />
        <Spacer size={16} />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextInput
              label={t("Cameras:labels_description")}
              placeholder={t("Organizations:instance_description_placeholder")}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        {selectedType?.provideUrl && (
          <>
            <Spacer size={16} />
            <Controller
              name="url"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextInput
                  required
                  inputRef={field.ref}
                  label={t("Common:url")}
                  placeholder={t("Common:url_placeholder")}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors.url && t("Errors:input_field_required")
                  }
                />
              )}
            />
          </>
        )}
        {selectedType?.provideApiKey && (
          <>
            <Spacer size={16} />
            <Controller
              name="apiKey"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TextInput
                  required
                  inputRef={field.ref}
                  label={t("Common:api_key_label")}
                  placeholder={t("Common:api_key_placeholder")}
                  description={t("Common:api_key_input_description")}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors.apiKey && t("Errors:input_field_required")
                  }
                />
              )}
            />
          </>
        )}
        <Spacer size={32} />
        {!!selectedType?.supportedOperations.length && (
          <>
            <Divider />
            <Spacer size={32} />
            <h2>{t("Common:endpoints")}</h2>
            <p>{t("Common:endpoints_description")}</p>
            <Spacer size={16} />
            {selectedType?.supportedOperations.includes("Create") && (
              <>
                <Controller
                  name="endpoints.create"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextInput
                      required
                      inputRef={field.ref}
                      label={t("Organizations:endpoint_create")}
                      description={t(
                        "Organizations:create_endpoint_input_description"
                      )}
                      value={field.value}
                      onChange={field.onChange}
                      validationError={
                        errors.endpoints?.create &&
                        t("Errors:input_field_required")
                      }
                    />
                  )}
                />
                <Spacer size={16} />
              </>
            )}
            {selectedType?.supportedOperations.includes("Update") && (
              <>
                <Controller
                  name="endpoints.update"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextInput
                      required
                      inputRef={field.ref}
                      label={t("Organizations:endpoint_update")}
                      description={t(
                        "Organizations:update_endpoint_input_description"
                      )}
                      value={field.value}
                      onChange={field.onChange}
                      validationError={
                        errors.endpoints?.update &&
                        t("Errors:input_field_required")
                      }
                    />
                  )}
                />
                <Spacer size={16} />
              </>
            )}
            {selectedType?.supportedOperations.includes("Delete") && (
              <>
                <Controller
                  name="endpoints.delete"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextInput
                      required
                      inputRef={field.ref}
                      label={t("Organizations:endpoint_delete")}
                      description={t(
                        "Organizations:delete_endpoint_input_description"
                      )}
                      value={field.value}
                      onChange={field.onChange}
                      validationError={
                        errors.endpoints?.delete &&
                        t("Errors:input_field_required")
                      }
                    />
                  )}
                />
                <Spacer size={16} />
              </>
            )}
            {selectedType?.supportedOperations.includes(
              "DeleteIntegration"
            ) && (
              <Controller
                name="endpoints.deleteIntegration"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextInput
                    required
                    inputRef={field.ref}
                    label={t("Organizations:deleteIntegration")}
                    description={t(
                      "Organizations:deleteIntegration_endpoint_input_description"
                    )}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors.endpoints?.deleteIntegration &&
                      t("Errors:input_field_required")
                    }
                  />
                )}
              />
            )}
            <Spacer size={32} />
          </>
        )}
        <CollapsibleBlock
          ref={integrationSettings}
          withScroll={!!getValues("integrationSettings")?.length}
          title={t("Organizations:integration_settings")}
        >
          <Spacer size={16} />
          <IntegrationSettings />
        </CollapsibleBlock>

        <Spacer size={16} />
        <Divider />
        <Spacer size={16} />

        <Column align="start">
          <CollapsibleBlock
            ref={customSettingRef}
            withScroll={!!getValues("expectedUserSettings")?.length}
            title={t("Common:custom_settings")}
          >
            <Spacer size={16} />
            <CustomSettings />
          </CollapsibleBlock>
        </Column>
      </FormProvider>
      <Spacer size={16} />
    </PrimeModal>
  );
};
